// Imperative meet-context fold (bundle tranche T3 follow-up, 2026-08-02).
//
// The meet fold (925bda76) deferred meet's ~280KB call/room/video/transcript
// tree behind the `/meet` + `/meet-guest` routes, keeping only a slim eager
// slice registered (MeetCallWidget + MeetControlBarIconButton +
// MeetNewRoomModal, and the connectCall/createRoom/meetRoomUrl/… functions).
// That inventory covered every EAGER caller — but it missed one caller that
// lives in ANOTHER LAZY package: chat's channel header opens the group call
// as a `@symbo.ls/windows` modal whose body is `extends: 'MeetVideoRoom'`
// (packages/chat/components/Main.js). Folding chat does NOT fold meet, so on
// /chat — with meet never visited — that key resolved against nothing and the
// modal opened EMPTY (DOMQL resolves a string `extends` at parse time, so a
// later meet fold cannot repair an already-parsed element).
//
// This is the meet-side twin of `ensureChatContext` (./chatFold.js): the same
// single-flight, idempotent, retry-on-failure fold through the one shared
// mechanism (`foldSharedLibContext`, shared/pages/lazyPage.js). Any surface
// that needs a meet component key it does not already have registered calls
// `el.call('ensureMeetContext')` and awaits it BEFORE mounting.
//
// Unlike the chat fold there is no slot to fill: meet has no always-mounted
// overlay that has to be materialised post-fold — the eager slice's
// MeetCallWidget is registered from boot, so the shell-root call dock is
// unaffected either way.
import _wsShared from '@symbo-ls/workspace-shared'
const shellFoldSharedLibContext = (...args) => _wsShared.pages.foldSharedLibContext(...args)
let _meetFoldInFlight = null

// Fold the full meet context into the live registry. Returns true once the
// context is registered, false when the fold failed — callers must bail
// quietly rather than mounting a body whose keys cannot resolve.
export const ensureMeetContext = async function () {
  // Same two-root split as ensureChatContext: foldSharedLibContext registers
  // into the ELEMENT root's `.context` (the contract every lazyPage route fold
  // uses), while the latch lives on the STATE root. Passing the state root to
  // the fold silently registers nothing.
  const rootEl = typeof this?.getRoot === 'function' ? this.getRoot() : this
  const stateRoot = this?.state?.root || rootEl?.state?.root || rootEl?.state || null
  if (!rootEl) return false
  if (stateRoot?._meetCtxFolded) return true
  if (!_meetFoldInFlight) {
    _meetFoldInFlight = import('@symbo-ls/workspace-meet/context.js')
      .then((mod) => {
        const lib = mod?.default || mod
        if (!lib || (!lib.components && !lib.functions)) {
          throw new Error('meet context resolved empty — stale/mismatched bundle')
        }
        shellFoldSharedLibContext(rootEl, lib)
        // Latch flips ONCE per app lifetime — the registry keeps the folded
        // components across tenant switches, so this key is deliberately NOT
        // in the workspace-scoped wipe registry (mirrors _chatCtxFolded).
        stateRoot?.update?.({ _meetCtxFolded: true }, { preventFetch: true })
        return true
      })
      .catch((err) => {
        _meetFoldInFlight = null
        console.error('[meet] context fold failed:', err?.message || err)
        return false
      })
  }
  return _meetFoldInFlight
}
